import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth.api';
import styles from './ResetPassword.module.css';

type PageState = 'loading' | 'valid' | 'invalid' | 'expired' | 'success';

interface ToastState {
    message: string;
    type: 'error' | 'success';
}

export const ResetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Page state
    const [pageState, setPageState] = useState<PageState>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Form state
    const [email, setEmail] = useState<string>('');
    const [agentId, setAgentId] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Toast state
    const [toast, setToast] = useState<ToastState | null>(null);

    // Validate URL parameters on mount
    useEffect(() => {
        const validateParams = () => {
            const aid = searchParams.get('aid');
            const m_uid = searchParams.get('m_uid');
            const ttl = searchParams.get('ttl');

            if (!aid || !m_uid || !ttl) {
                setErrorMessage('The password reset link is invalid or missing required parameters.');
                setPageState('invalid');
                return;
            }

            try {
                // Decode base64 email
                const decodedEmail = atob(m_uid);

                // Decode base64 TTL and check expiration
                const decodedTtl = atob(ttl);
                const expirationTime = parseInt(decodedTtl, 10);
                const currentTime = Math.floor(Date.now() / 1000);

                if (isNaN(expirationTime)) {
                    setErrorMessage('The password reset link is malformed.');
                    setPageState('invalid');
                    return;
                }

                if (currentTime > expirationTime) {
                    setErrorMessage('This password reset link has expired. Please request a new one.');
                    setPageState('expired');
                    return;
                }

                // Valid parameters
                setAgentId(aid);
                setEmail(decodedEmail);
                setPageState('valid');
            } catch (error) {
                console.error('Failed to decode URL parameters:', error);
                setErrorMessage('The password reset link is invalid or corrupted.');
                setPageState('invalid');
            }
        };

        // Small delay to show loading state
        const timer = setTimeout(validateParams, 500);
        return () => clearTimeout(timer);
    }, [searchParams]);

    // Auto-hide toast after 3 seconds
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Auto-redirect to login after showing invalid/expired state
    useEffect(() => {
        if (pageState === 'invalid' || pageState === 'expired') {
            const timer = setTimeout(() => {
                navigate('/agent-login');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [pageState, navigate]);

    // Auto-redirect to login after success
    useEffect(() => {
        if (pageState === 'success') {
            const timer = setTimeout(() => {
                navigate('/agent-login');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [pageState, navigate]);

    const showToast = (message: string, type: 'error' | 'success') => {
        setToast({ message, type });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!newPassword || !confirmPassword) {
            showToast('Please fill in all fields', 'error');
            return;
        }

        if (newPassword.length < 6) {
            showToast('Password must be at least 6 characters', 'error');
            return;
        }

        if (newPassword !== confirmPassword) {
            showToast('Passwords do not match', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await authApi.resetPassword(email, newPassword, agentId);

            if (response.success) {
                setPageState('success');
            } else {
                showToast(response.message || 'Failed to reset password', 'error');
            }
        } catch (error) {
            console.error('Reset password error:', error);
            showToast('Failed to reset password. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/agent-login');
    };

    // Render loading state
    if (pageState === 'loading') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.spinner}></div>
                        <p className={styles.loadingText}>Validating reset link...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Render error states (invalid or expired)
    if (pageState === 'invalid' || pageState === 'expired') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.errorContainer}>
                        <div className={styles.errorIcon}>
                            {pageState === 'expired' ? '⏰' : '⚠️'}
                        </div>
                        <h1 className={styles.errorTitle}>
                            {pageState === 'expired' ? 'Link Expired' : 'Invalid Link'}
                        </h1>
                        <p className={styles.errorMessage}>{errorMessage}</p>
                        <button className={styles.backButton} onClick={handleBackToLogin}>
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Render success state
    if (pageState === 'success') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.successContainer}>
                        <div className={styles.successIcon}>✅</div>
                        <h1 className={styles.successTitle}>Password Reset Successful!</h1>
                        <p className={styles.successMessage}>
                            Your password has been updated successfully.<br />
                            Redirecting you to login...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Render password reset form
    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Reset Password</h1>
                    <p className={styles.subtitle}>Create a new password for your account</p>
                </div>

                <div className={styles.emailInfo}>
                    <span className={styles.emailIcon}>📧</span>
                    <div>
                        <p className={styles.emailLabel}>Resetting password for</p>
                        <p className={styles.emailValue}>{email}</p>
                    </div>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.inputGroup}>
                        <label className={styles.label} htmlFor="newPassword">
                            New Password
                        </label>
                        <div className={styles.inputWrapper}>
                            <input
                                id="newPassword"
                                type={showPassword ? 'text' : 'password'}
                                className={styles.input}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter your new password"
                                minLength={6}
                                disabled={isSubmitting}
                                autoFocus
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                            >
                                {showPassword ? '🙈' : '👁️'}
                            </button>
                        </div>
                        <span className={styles.requirements}>Minimum 6 characters</span>
                    </div>

                    <div className={styles.inputGroup}>
                        <label className={styles.label} htmlFor="confirmPassword">
                            Confirm New Password
                        </label>
                        <div className={styles.inputWrapper}>
                            <input
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                className={styles.input}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm your new password"
                                minLength={6}
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                tabIndex={-1}
                            >
                                {showConfirmPassword ? '🙈' : '👁️'}
                            </button>
                        </div>
                    </div>

                    <div className={styles.actions}>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={
                                isSubmitting ||
                                !newPassword ||
                                !confirmPassword ||
                                newPassword.length < 6
                            }
                        >
                            {isSubmitting ? 'Resetting...' : 'Reset Password'}
                        </button>
                        <button
                            type="button"
                            className={styles.backButton}
                            onClick={handleBackToLogin}
                            disabled={isSubmitting}
                        >
                            Back to Login
                        </button>
                    </div>
                </form>
            </div>

            {/* Toast notification */}
            {toast && (
                <div
                    className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess
                        }`}
                >
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default ResetPassword;
